import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchProviderProfileContact, ProviderProfileContact } from '@/services/providerProfileService';
import { debugLogger } from '@/utils/debug';

export interface UnifiedContactInfo extends ProviderProfileContact {
  source: 'listing' | 'profile' | 'none';
}

interface UseContactInfoParams {
  userId?: string;
  listingWhatsApp?: string | null;
}

/**
 * Consolidated contact resolution logic.
 * Priority:
 * 1. Listing-level contact fields (unassigned listing scenario)
 * 2. Provider profile contact (cached service)
 * 3. None
 */
export function useContactInfo({ userId, listingWhatsApp }: UseContactInfoParams) {
  const [data, setData] = useState<UnifiedContactInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // React Query fetch when userId present and no listing override
  const { data: profileData, isFetching: profileLoading } = useQuery({
    queryKey: ['contactInfo', userId],
    queryFn: async () => {
      if (!userId) return null;
      return fetchProviderProfileContact(userId);
    },
    enabled: !!userId && !listingWhatsApp,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  useEffect(() => {
    // Listing override
    if (listingWhatsApp) {
      setData({
        whatsapp_number: listingWhatsApp || null,
        whatsapp_verified: false,
        source: 'listing'
      });
      setLoading(false);
      return;
    }
    // No user
    if (!userId) {
      setData({
        whatsapp_number: null,
        whatsapp_verified: false,
        source: 'none'
      });
      setLoading(false);
      return;
    }
    // Profile via query
    if (!profileLoading) {
      if (profileData) {
        setData({ ...profileData, source: 'profile' });
      } else {
        setData({
          whatsapp_number: null,
          whatsapp_verified: false,
          source: 'none'
        });
      }
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [listingWhatsApp, userId, profileData, profileLoading]);

  return { contactInfo: data, loading };
}

// Backwards compatibility export (will remove once migrated)